import React from "react";

/**
 * StatPair — shared evidence component for booking_horizon_split and promise_vs_delivery.
 *
 * Two side-by-side stat blocks. Adapts layout based on panel key.
 *
 * booking_horizon_split: "Could see a forecast" vs "Booked too far ahead"
 * promise_vs_delivery: "Promised" vs "Actually got" with signed gap.
 *
 * Spec references: §7 (booking_horizon_split, promise_vs_delivery).
 */

const fmtNum = (v) => Number(v ?? 0).toLocaleString();

const StatPair = ({ panel }) => {
  const f = panel.facts || {};

  // ─── promise_vs_delivery ──────────────────────────────────────────────────
  // Mirrors the ComparisonPair (weather_regret) layout: two stats side by side
  // with a vertical divider. The signed gap, stays-worse count, and caption
  // sit below as muted FYI lines.
  if (panel.panel === "promise_vs_delivery") {
    const promised = Number(f.avg_promised_high_f) || 0;
    const delivered = Number(f.avg_delivered_high_f) || 0;
    const gap = Number(f.avg_gap_f) || 0;
    const worse = Number(f.stays_worse_than_promised) || 0;
    const compared = Number(f.stays_compared) || 0;

    const gapColor = gap > 0 ? "text-orange-500" : gap < 0 ? "text-blue-500" : "text-gray-500";

    return (
      <div>
        {/* Two stats side by side with a divider — mirrors ComparisonPair */}
        <div className="flex items-baseline justify-center gap-6">
          <div className="text-right">
            <span className="text-4xl font-bold text-gray-900 dark:text-gray-100 leading-none">
              {promised}°
            </span>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Promised
            </p>
          </div>
          <div className="w-px h-12 bg-gray-200 dark:bg-gray-700 self-center" />
          <div className="text-left">
            <span className="text-4xl font-bold text-gray-900 dark:text-gray-100 leading-none">
              {delivered}°
            </span>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Actually got
            </p>
          </div>
        </div>

        {/* Signed gap */}
        <p className={`text-center text-sm font-semibold ${gapColor} mt-3`}>
          {gap > 0 ? "+" : ""}{gap}° average gap
        </p>

        {/* Stays worse — styled like ComparisonPair's "Worst example:" caption */}
        <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-3 text-center italic">
          {fmtNum(worse)} stays came in worse than promised
        </p>

      </div>
    );
  }

  // ─── booking_horizon_split (default) ──────────────────────────────────────
  // Mirrors the HeroNumber (weather_loss) layout: two centered hero figures
  // separated by a vertical divider, with a muted caption beneath.
  const couldSee = Number(f.sessions_that_could_see_a_forecast) || 0;
  const couldSeeBookings = Number(f.bookings_from_those_sessions) || 0;
  const tooFarAhead = Number(f.sessions_booking_too_far_ahead_to_see_one) || 0;
  const tooFarBookings = Number(f.bookings_from_those_sessions_far_ahead) || 0;

  return (
    <div>
      {/* Two stats side by side — compact, not full width */}
      <div className="flex items-baseline justify-center gap-6">
          <div className="text-right">
            <span className="text-4xl font-bold text-gray-700 dark:text-gray-300 leading-none">
              {fmtNum(couldSee)}
            </span>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              could see a forecast
            </p>
            <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
              {fmtNum(couldSeeBookings)} bookings
            </p>
          </div>
          <div className="w-px h-12 bg-gray-200 dark:bg-gray-700 self-center" />
          <div className="text-left">
            <span className="text-4xl font-bold text-gray-700 dark:text-gray-300 leading-none">
              {fmtNum(tooFarAhead)}
            </span>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            booked too far ahead
          </p>
          <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
            {fmtNum(tooFarBookings)} bookings
          </p>
        </div>
      </div>

      {/* FYI-style muted caption */}
      <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-3 text-center italic">
        {fmtNum(f.total_sessions)} total sessions · {fmtNum(f.total_bookings)} total bookings
      </p>
    </div>
  );
};

export default StatPair;
